/**
 * The purchase dialog of the shop (port addition): a tap on a pack of the exchange screen's "Buy" block (or on the
 * starter pack) opens it over the screen, so that on a phone, where there is no hover, the player sees what the pack
 * gives before anything happens. The frame of the game's popups (menu_tired's, as the account and delete dialogs)
 * and in it:
 *  - the title (buy diamonds / buy gold / the starter pack), the pack's icons with its amounts, its badge;
 *  - what the pack gives in words: the bonus over the base rate, the first purchase x2, the contents and the
 *    once-per-account rule of the starter pack;
 *  - the price, "Buy" (the green button of the game's popups) and "cancel".
 * "Buy" runs the purchase (the host's store: Google Play's own purchase sheet, the development provider, or nothing
 * yet); the answer closes the dialog (credited, cancelled) or turns it into a message with "OK": the skeleton of the
 * shop says that payment is not connected yet and that nothing was charged or credited.
 *
 * Every text arrives from runtime/monetization-ui.ts (the model), so this module has no texts of its own.
 * Escape and the Android back button cancel (not while the purchase is running). A wide screen (a phone, a tablet)
 * shows the dialog larger, and the screen under it is hidden while it is open (ui/port-dialog.ts).
 */
import { Event, KeyboardEvent, Sprite, TextField } from '@flash';
import { BigButton, LABEL_COLOR, LINK_COLOR, SmallButton, TEXT_COLOR, label, popupFrame, setRawText, shrinkToFit } from './controls';
import { fitDialogBody, hideScreenUnder } from './port-dialog';

/** What the dialog shows: the texts in the player's language, the buttons' labels as source texts (translated by the runtime). */
export interface StoreDialogModel {
  title: string;
  /** The pack's currencies: an icon (a detached sprite of the game's art, or null) and its amount. */
  icons: Array<{ icon: any; amount: string }>;
  /** The badge beside the icons ("+20 %"), or null. */
  badge: string | null;
  /** The tag above the icons ("Popular"), or null. */
  tag: string | null;
  /** What the pack gives in words, one line each. */
  lines: string[];
  price: string;
  /** The buttons' labels (source texts). */
  buy: string;
  cancel: string;
  ok: string;
  wait: string;
}

/** The answer to "Buy": close the dialog, or show `message` (lines; the first one bold) with "OK". */
export type StoreDialogOutcome = { close: true } | { close: false; message: string[] };

export interface StoreDialogOptions {
  /** Runs the purchase; never rejects. */
  buy(): Promise<StoreDialogOutcome>;
  /** The dialog is gone (for any reason). */
  onClosed(): void;
}

/** The frame (menu_tired's at 0.8, 344 x 389) in the middle of the 807 x 600 stage, and its content column. */
const PANEL_SCALE = 0.8;
const FRAME_W = 344;
const FRAME_H = 389;
const FRAME_X = Math.round((807 - FRAME_W) / 2);
const FRAME_Y = Math.round((600 - FRAME_H) / 2);
const COL_X = 26;
const COL_W = 270;
const DIM = { x: -2000, y: -1000, w: 4807, h: 2600, alpha: 0.5 };
/** The badge's red (the "+" crosses of the main map) and the tag's green (the popups' links). */
export const BADGE_COLOR = 0xd01818;
export const TAG_COLOR = LINK_COLOR;
/** The message of a purchase that did not happen: the dark brown of the receipt's texts. */
const MESSAGE_COLOR = 0x5a2d00;

/** A sticker: bold white text on a rounded plate of `color` (the badges of the shop's packs). */
export function sticker(text: string, color: number, size = 11): Sprite {
  const s = new Sprite();
  s.mouseEnabled = false;
  s.mouseChildren = false;
  const tf = label('', { size, bold: true, color: 0xffffff });
  setRawText(tf, text);
  const w = Math.ceil(tf.textWidth) + 12;
  const h = Math.ceil(tf.textHeight) + 4;
  s.graphics.lineStyle(1.5, 0xffffff, 1);
  s.graphics.beginFill(color, 1);
  s.graphics.drawRoundRect(0, 0, w, h, h, h);
  s.graphics.endFill();
  tf.x = 4;
  tf.y = 0;
  s.addChild(tf);
  return s;
}

export class StoreDialog {
  /** Everything of the dialog (added to the game's system layer by FWP.net_store_dialog). */
  readonly root = new Sprite();
  readonly buyButton: BigButton;
  readonly cancelButton: SmallButton;
  readonly okButton: BigButton;
  readonly priceField: TextField;
  readonly messageField: TextField;
  /** The purchase is running. */
  busy = false;
  /** "Buy" presses that started a purchase (tests: one at a time). */
  purchases = 0;

  private disposed = false;
  private stageRef: any = null;
  private readonly body = new Sprite();
  private readonly details = new Sprite();
  private readonly restoreUnder: () => void;

  constructor(
    fwp: any,
    private readonly model: StoreDialogModel,
    private readonly opts: StoreDialogOptions,
  ) {
    this.root.name = '$store_dialog';
    const dim = new Sprite();
    dim.name = '$store_dialog_dim';
    dim.graphics.beginFill(0x000000, DIM.alpha);
    dim.graphics.drawRect(DIM.x, DIM.y, DIM.w, DIM.h);
    dim.graphics.endFill();
    this.root.addChild(dim);
    this.body.name = '$store_dialog_body';
    this.root.addChild(this.body);
    const panel = popupFrame(fwp, FRAME_X, FRAME_Y, PANEL_SCALE);
    panel.name = '$store_dialog_panel';
    this.body.addChild(panel);
    const page = new Sprite();
    page.name = '$store_dialog_content';
    page.x = FRAME_X;
    page.y = FRAME_Y;
    this.body.addChild(page);
    this.restoreUnder = hideScreenUnder(fwp);

    const title = raw(label('', { size: 16, bold: true, color: LABEL_COLOR, align: 'center', width: COL_W }), 'store_dialog_title', model.title);
    shrinkToFit(title, 11);
    place(page, title, COL_X, 18);

    // The details (hidden by a message): the icons with their amounts, the badge and the tag, the lines.
    this.details.name = 'store_dialog_details';
    page.addChild(this.details);
    if (model.tag) {
      const tag = named(sticker(model.tag, TAG_COLOR, 12), 'store_dialog_tag');
      place(this.details, tag, COL_X + (COL_W - tag.width) / 2, 44);
    }
    const row = new Sprite();
    row.name = 'store_dialog_amounts';
    let x = 0;
    for (const it of model.icons) {
      if (it.icon) {
        it.icon.scaleX = it.icon.scaleY = 1.5;
        const b = it.icon.getBounds(it.icon);
        it.icon.x = x - b.x * 1.5;
        it.icon.y = 18 - (b.y + b.height / 2) * 1.5;
        row.addChild(it.icon);
        x += b.width * 1.5 + 6;
      }
      const amount = raw(label('', { size: 26, bold: true, color: TEXT_COLOR }), 'store_dialog_amount', it.amount);
      amount.x = x;
      amount.y = 18 - amount.textHeight / 2 - 2;
      row.addChild(amount);
      x += amount.textWidth + 18;
    }
    if (model.badge) {
      const badge = named(sticker(model.badge, BADGE_COLOR, 13), 'store_dialog_badge');
      badge.x = x - 8;
      badge.y = 18 - badge.height / 2;
      row.addChild(badge);
      x += badge.width;
    }
    // The row in the column's middle (smaller when two currencies and a badge do not fit).
    const k = Math.min(1, COL_W / Math.max(1, x));
    row.scaleX = row.scaleY = k;
    place(this.details, row, COL_X + (COL_W - x * k) / 2, model.tag ? 72 : 60);
    const lines = raw(label('', { size: 13, color: TEXT_COLOR, align: 'center', width: COL_W, lines: 5 }), 'store_dialog_lines', model.lines.join('\n'));
    lines.height = 96;
    shrinkToFit(lines, 10);
    place(this.details, lines, COL_X, 114);

    this.messageField = raw(label('', { size: 15, bold: true, color: MESSAGE_COLOR, align: 'center', width: COL_W, lines: 6 }), 'store_dialog_message', '');
    this.messageField.height = 150;
    this.messageField.visible = false;
    place(page, this.messageField, COL_X, 70);

    this.priceField = raw(label('', { size: 22, bold: true, color: TEXT_COLOR, align: 'center', width: COL_W }), 'store_dialog_price', model.price);
    shrinkToFit(this.priceField, 14);
    place(page, this.priceField, COL_X, 214);

    this.buyButton = new BigButton(fwp, 'store_dialog_buy', model.buy, 'green');
    this.buyButton.addEventListener('click', () => this.submit());
    place(page, this.buyButton, COL_X + (COL_W - this.buyButton.width) / 2, 256);
    this.okButton = new BigButton(fwp, 'store_dialog_ok', model.ok, 'green');
    this.okButton.addEventListener('click', () => this.close());
    this.okButton.visible = false;
    place(page, this.okButton, COL_X + (COL_W - this.okButton.width) / 2, 256);
    this.cancelButton = new SmallButton(fwp, 'store_dialog_cancel', model.cancel);
    this.cancelButton.addEventListener('click', () => this.close());
    place(page, this.cancelButton, COL_X + (COL_W - this.cancelButton.width) / 2, 310);
    // A finger's target from under "Buy" to the bottom of the frame's content, across the column.
    this.cancelButton.setHitRect({ x: COL_X - this.cancelButton.x, y: 303 - 310, width: COL_W, height: 42 });
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
  }

  /** Starts listening to the keyboard (once the dialog is on the stage). */
  attach(): void {
    const st = this.root.stage;
    if (!st || this.stageRef) return;
    this.stageRef = st;
    st.addEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
  }

  /** Back (the Android back button): closes unless the purchase is running. Always handled while open. */
  back(): boolean {
    if (!this.busy) this.close();
    return true;
  }

  /** Closes the dialog (not while the purchase is running). */
  close(): void {
    if (this.busy || this.disposed) return;
    this.dispose();
  }

  /** "Buy": one purchase at a time; its answer closes the dialog or shows its message. */
  submit(): void {
    if (this.busy || this.disposed) return;
    this.busy = true;
    this.purchases++;
    this.buyButton.enabled = false;
    this.buyButton.setText(this.model.wait);
    this.cancelButton.enabled = false;
    void this.opts.buy().then((r) => {
      if (this.disposed) return;
      this.busy = false;
      if (r.close) {
        this.dispose();
        return;
      }
      this.showMessage(r.message);
    });
  }

  /** The dialog as a message: its lines (the first bold, the rest plain) and "OK". */
  showMessage(message: string[]): void {
    this.details.visible = false;
    this.priceField.visible = false;
    this.buyButton.visible = false;
    this.cancelButton.visible = false;
    this.okButton.visible = true;
    const [head, ...rest] = message;
    setRawText(this.messageField, [head, ...rest].join('\n'));
    // The first line bold and larger, the rest in the body's plain text.
    const tf = this.messageField;
    if (rest.length) {
      const from = head.length + 1;
      const f = tf.getTextFormat(from, tf.text.length);
      f.bold = false;
      f.size = 13;
      f.color = TEXT_COLOR;
      tf.setTextFormat(f, from, tf.text.length);
    }
    shrinkToFit(tf, 10);
    // In the middle of the space above "OK".
    tf.y = Math.round(70 + Math.max(0, (170 - tf.textHeight) / 2));
    tf.visible = true;
  }

  get message(): string {
    return this.messageField.visible ? this.messageField.text : '';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.stageRef) this.stageRef.removeEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
    if (this.root.parent) this.root.parent.removeChild(this.root);
    this.restoreUnder();
    this.opts.onClosed();
  }

  get closed(): boolean {
    return this.disposed;
  }

  private readonly onKey = (e: any): void => {
    if (this.disposed) return;
    if (e.keyCode === 27) this.close();
    else if (e.keyCode === 13) {
      if (this.okButton.visible) this.close();
      else this.submit();
    }
  };

  private readonly onFrame = (): void => {
    fitDialogBody(this.body, { x: FRAME_X, y: FRAME_Y, width: FRAME_W, height: FRAME_H }, this.root.stage);
  };
}

function place<T extends { x: number; y: number }>(parent: Sprite, child: T, x: number, y: number): T {
  child.x = Math.round(x);
  child.y = Math.round(y);
  parent.addChild(child as any);
  return child;
}

function named<O extends { name: string }>(o: O, name: string): O {
  o.name = name;
  return o;
}

/** A field of data or of text translated already: shown as given. */
function raw(tf: TextField, name: string, text: string): TextField {
  tf.name = name;
  setRawText(tf, text);
  return tf;
}
